﻿import { useEffect, useRef } from "react";
import "./LiquidBackground.css";

declare global {
  interface Window {
    LiquidBG?: {
      setTheme: (name: string) => void;
      setSpeed: (value: number) => void;
      pause: () => void;
      play: () => void;
      refresh: () => void;
      destroy: () => void;
    };
  }
}

export function LiquidBackground() {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const root = document.documentElement;
    if (!host || !canvas) return;

    host.dataset.renderer = "css";

    const MAX_PIXELS = 800000;
    const TARGET_FPS = 30;
    const TAU = Math.PI * 2;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reduceMotion = reduced.matches;
    const cleanupFns: (() => void)[] = [];
    const current = new Float32Array(15);
    const target = new Float32Array(15);
    const sample = document.createElement("canvas");
    sample.width = sample.height = 1;
    const sampleCtx = sample.getContext("2d", { willReadFrequently: true });

    let gl: WebGLRenderingContext | null = null;
    let program: WebGLProgram | null = null;
    let buffer: WebGLBuffer | null = null;
    let uniforms: Record<string, WebGLUniformLocation | null> = {};
    let ready = false;
    let destroyed = false;
    let paused = false;
    let raf = 0;
    let last = 0;
    let clock = 12;
    let speed = 1.5;
    let warp = 0.85;

    const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
    const number = (value: string, fallback: number) =>
      Number.isFinite(parseFloat(value)) ? parseFloat(value) : fallback;

    function listen<K extends keyof WindowEventMap>(
      el: EventTarget,
      event: string,
      fn: (e: any) => void,
      options?: boolean | AddEventListenerOptions
    ) {
      el.addEventListener(event, fn, options);
      cleanupFns.push(() => el.removeEventListener(event, fn, options));
    }

    function stopFrame() {
      cancelAnimationFrame(raf);
      raf = 0;
      last = 0;
    }

    function wake() {
      if (!raf && ready && !destroyed && !document.hidden) {
        raf = requestAnimationFrame(frame);
      }
    }

    function moving() {
      return !paused && !reduceMotion && speed > 0;
    }

    function syncUI() {
      if (!host) return;
      host.dataset.paused = String(!moving() || document.hidden);
      host.style.setProperty("--fallback-duration", `${40 / Math.max(speed, 0.05)}s`);
    }

    function readTheme(immediate = false) {
      if (destroyed) return;
      const css = getComputedStyle(root);
      for (let i = 0; i < 5; i++) {
        const value = css.getPropertyValue(`--flow-${i}`).trim();
        if (sampleCtx) {
          sampleCtx.clearRect(0, 0, 1, 1);
          sampleCtx.fillStyle = "#000000";
          sampleCtx.fillStyle = value || (i === 0 ? "#100e11" : "#f46c28");
          sampleCtx.fillRect(0, 0, 1, 1);
          const rgba = sampleCtx.getImageData(0, 0, 1, 1).data;
          for (let j = 0; j < 3; j++) {
            target[i * 3 + j] = rgba[j]! / 255;
          }
        }
      }
      speed = clamp(number(css.getPropertyValue("--flow-speed"), 1.5), 0, 1.5);
      warp = clamp(number(css.getPropertyValue("--flow-warp"), 0.85), 0, 1.5);
      if (immediate || reduceMotion) current.set(target);

      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        const flow0 = css.getPropertyValue("--flow-0").trim();
        if (flow0) meta.setAttribute("content", flow0);
      }
      syncUI();
      wake();
    }

    const vertexShaderSrc = `
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
        vUv = aPosition * .5 + .5;
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    const fragmentShaderSrc = `
      #ifdef GL_FRAGMENT_PRECISION_HIGH
        precision highp float;
      #else
        precision mediump float;
      #endif
      varying vec2 vUv;
      uniform vec2 uResolution;
      uniform vec4 uPhase;
      uniform vec3 uColors[5];
      uniform float uWarp;
      float band(float position, float width) {
        float x = position / width;
        return exp(-x * x);
      }
      void main() {
        vec2 p = vUv - .5;
        p.x *= clamp(uResolution.x / uResolution.y, .65, 2.2);
        vec2 q = p + uWarp * vec2(
          .28 * sin(p.y * 2.8 + uPhase.x) + .18 * cos(p.x * 2.1 - uPhase.y),
          .27 * cos(p.x * 2.7 - uPhase.y) + .14 * sin(p.y * 2.2 + uPhase.z)
        );
        float s = .85 * q.x + .53 * q.y;
        float broad = band(s - .30 - .09 * sin(q.y * 3.0 + uPhase.z), .40);
        float shadow = band(s + .24 + .10 * cos(q.y * 2.5 - uPhase.w), .25);
        float silk = band(s - .05 + .11 * sin(q.x * 2.0 + uPhase.x), .12);
        float base = .5 + .5 * sin(q.x * 1.4 - q.y * 1.6 + uPhase.w);
        vec2 lightPosition = (q - vec2(.9, -.38)) / vec2(.72, .65);
        float light = exp(-dot(lightPosition, lightPosition));
        vec3 color = mix(uColors[0], uColors[1], base * .80);
        color = mix(color, uColors[2], broad * .97);
        color = mix(color, uColors[3], shadow * .85);
        color = mix(color, uColors[4], silk * .58 * (.6 + .4 * cos(q.y * 2.0 - uPhase.y)));
        color = mix(color, uColors[4], light * .48);
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    function initGL() {
      try {
        gl = canvas!.getContext("webgl", {
          alpha: true,
          antialias: false,
          depth: false,
          stencil: false,
          powerPreference: "low-power",
          preserveDrawingBuffer: false,
        });
        if (!gl || !sampleCtx) return;

        const shaders: WebGLShader[] = [];
        program = gl.createProgram();
        if (!program) return;

        for (const [type, source] of [
          [gl.VERTEX_SHADER, vertexShaderSrc],
          [gl.FRAGMENT_SHADER, fragmentShaderSrc],
        ] as const) {
          const shader = gl.createShader(type);
          if (!shader) return;
          gl.shaderSource(shader, source);
          gl.compileShader(shader);
          gl.attachShader(program, shader);
          shaders.push(shader);
        }

        gl.bindAttribLocation(program, 0, "aPosition");
        gl.linkProgram(program);
        const linked = gl.getProgramParameter(program, gl.LINK_STATUS);
        const error = linked ? "" : gl.getProgramInfoLog(program);
        shaders.forEach((shader) => gl!.deleteShader(shader));
        if (!linked) throw new Error(error || "Shader link failed");

        gl.useProgram(program);
        buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

        uniforms = {};
        for (const name of ["uResolution", "uPhase", "uColors[0]", "uWarp"]) {
          uniforms[name] = gl.getUniformLocation(program, name);
        }

        ready = true;
        resize();
        wake();
      } catch (error) {
        ready = false;
        host!.dataset.renderer = "css";
        if (gl && buffer) gl.deleteBuffer(buffer);
        if (gl && program) gl.deleteProgram(program);
        console.warn("LiquidBG: fallback to CSS gradient", error);
      }
    }

    function resize() {
      if (!ready || destroyed || !canvas || !host || !gl) return;
      const rect = host.getBoundingClientRect();
      const scale = Math.min(1, Math.sqrt(MAX_PIXELS / Math.max(1, rect.width * rect.height)));
      const width = Math.max(1, Math.floor(rect.width * scale));
      const height = Math.max(1, Math.floor(rect.height * scale));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, width, height);
      wake();
    }

    function frame(now: number) {
      raf = 0;
      if (!ready || destroyed || document.hidden || !gl) return;
      if (last && now - last < 1000 / TARGET_FPS - 0.5) {
        wake();
        return;
      }
      const dt = last ? Math.min((now - last) / 1000, 0.1) : 1 / TARGET_FPS;
      last = now;
      if (moving()) clock += dt * speed;

      const blend = reduceMotion ? 1 : 1 - Math.exp(-dt * 5);
      let settling = false;
      for (let i = 0; i < 15; i++) {
        const difference = target[i]! - current[i]!;
        if (Math.abs(difference) > 0.0005) {
          current[i]! += difference * blend;
          settling = true;
        } else {
          current[i] = target[i]!;
        }
      }

      gl.uniform2f(uniforms.uResolution!, canvas!.width, canvas!.height);
      gl.uniform4f(
        uniforms.uPhase!,
        (clock * 0.17) % TAU,
        (clock * 0.11) % TAU,
        (clock * 0.07) % TAU,
        (clock * 0.13) % TAU
      );
      gl.uniform3fv(uniforms["uColors[0]"]!, current);
      gl.uniform1f(uniforms.uWarp!, warp);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (host!.dataset.renderer !== "webgl") host!.dataset.renderer = "webgl";
      if (moving() || settling) wake();
      else last = 0;
    }

    const api = {
      setTheme(name: string) {
        if (!destroyed) root.dataset.theme = String(name);
      },
      setSpeed(value: number) {
        if (!destroyed && Number.isFinite(Number(value))) {
          root.style.setProperty("--flow-speed", String(clamp(Number(value), 0, 1.5)));
        }
      },
      pause() {
        if (!destroyed) {
          paused = true;
          syncUI();
          wake();
        }
      },
      play() {
        if (!destroyed) {
          paused = false;
          syncUI();
          wake();
        }
      },
      refresh() {
        readTheme();
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        ready = false;
        stopFrame();
        observer.disconnect();
        resizeObserver?.disconnect();
        cleanupFns.forEach((fn) => fn());
        if (gl && buffer) gl.deleteBuffer(buffer);
        if (gl && program) gl.deleteProgram(program);
        if (host) {
          host.dataset.renderer = "css";
          host.dataset.paused = "true";
        }
      },
    };

    window.LiquidBG = api;

    const observer = new MutationObserver(() => readTheme());
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme", "class", "style"] });

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(host);

    listen(window, "resize", resize, { passive: true });
    listen(document, "visibilitychange", () => {
      stopFrame();
      syncUI();
      wake();
    });
    listen(reduced, "change", (e: MediaQueryListEvent) => {
      reduceMotion = e.matches;
      stopFrame();
      readTheme(true);
    });
    listen(canvas, "webglcontextlost", (e: Event) => {
      e.preventDefault();
      ready = false;
      stopFrame();
      if (host) host.dataset.renderer = "css";
    });
    listen(canvas, "webglcontextrestored", () => {
      initGL();
      readTheme(true);
    });

    readTheme(true);
    initGL();

    return () => {
      api.destroy();
      if (window.LiquidBG === api) {
        delete window.LiquidBG;
      }
    };
  }, []);

  return (
    <div id="liquid-bg" aria-hidden="true" ref={hostRef}>
      <div className="liquid-fallback" />
      <canvas ref={canvasRef} />
      <div className="liquid-grain" />
      <div className="liquid-veil" />
    </div>
  );
}
